import React, { PropTypes } from "react";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Field, change as changeFieldValue, formValueSelector, reduxForm } from "redux-form";
import validationUtils from "../../../../../utils/ValidationUtils";
import * as dataActions from "../../../../../utils/data/dataActions";
import { MessageBox } from "../../../../common";
import { getRetroactiveCredits } from "../../../../common/RetroactiveData/RetroactiveLimitHelper";
import { convertMonthsToYears } from "../../../../common/dateHelper";
import { SelectInput, TextInput } from "../../../../common/form";
import { RectangleSpinner } from "../../../../common/spinners";
import { IMPLEMENTATION, TERMINATED } from "../../enums/clientStatusTypes";
import { INVOICING_CONFIG_VALIDATION_RULES } from "./invoicingConfigurationValidationRules";
import userHasAccess from "../../../../../utils/userHasAccess";
import { FINTECH } from "../../../../../utils/Roles";


class InvoicingConfigurationForm extends React.Component {
    static generateValidationRules(props) {
        let newRules = { ...INVOICING_CONFIG_VALIDATION_RULES };
        if (props.client.clientStatusType !== IMPLEMENTATION.value) {
            newRules["creditsInvoiceFrequency"] = {
                ...newRules["creditsInvoiceFrequency"],
                isRequiredTrim: { value: true },
            };
        }
        return newRules;
    }

    constructor(props, context) {
        super(props, context);

        this.state = {
            retroactiveCredits: {},
            frequency: undefined,
            invoiceCount: undefined,
        };
        this.generateCreditsInvoiceFrequencyOptions = this.generateCreditsInvoiceFrequencyOptions.bind(this);
        this.setMaximumRetroactiveCredits = this.setMaximumRetroactiveCredits.bind(this);
        this.onChangeFrequency = this.onChangeFrequency.bind(this);
    }

    componentDidMount() {
        this.setState({
            frequency: this.props.initialValues.creditsInvoiceFrequency,
            invoiceCount: this.props.initialValues.creditsInvoiceCount,
        });
        this.setMaximumRetroactiveCredits();
    }

    setMaximumRetroactiveCredits() {
        getRetroactiveCredits(this.props.client.clientId, (result) => {
            this.setState({
                retroactiveCredits: result
            });
        });
    }

    generateCreditsInvoiceFrequencyOptions() {
        const nbMonthsRetroactiveLimitation = this.state.retroactiveCredits && this.state.retroactiveCredits.nbMonthsRetroactiveLimitation;
        const creditInvoiceFrequency = this.state.retroactiveCredits && this.state.retroactiveCredits.creditInvoiceFrequency;
        const options = [];
        if (nbMonthsRetroactiveLimitation && creditInvoiceFrequency) {
            // The formula to generate maximum limit is nbMonthsRetroactiveLimitation / creditInvoiceFrequency
            const maximumValueForList = nbMonthsRetroactiveLimitation / creditInvoiceFrequency;
            for (let i = 1; i <= maximumValueForList; i++) {
                if (nbMonthsRetroactiveLimitation % i === 0) {
                    // We only give the multiples of maximum limit as options
                    options.push({
                        text: ("0" + i.toString()).slice(-2), // The key is the index + 1, converted to a string
                        value: i, // The value is taken from the `values` array
                    });
                }
            }
            return options;
        }
    }

    onChangeFrequency(event) {
        const creditsInvoiceFrequency = event.target.value;
        const nbMonthsRetroactiveLimitation = this.state.retroactiveCredits && this.state.retroactiveCredits.nbMonthsRetroactiveLimitation;
        // The formula to calculate creditsInvoiceCount is nbMonthsRetroactiveLimitation / creditsInvoiceFrequency ( selected value )
        const creditsInvoiceCount = (nbMonthsRetroactiveLimitation / Number(creditsInvoiceFrequency));
        this.props.changeFieldValue("invoicingConfiguration", "creditsInvoiceCount", creditsInvoiceCount);
        this.setState({
            frequency: creditsInvoiceFrequency,
            invoiceCount: creditsInvoiceCount,
        });
    }

    render() {
        const { handleSubmit, submitting, pristine } = this.props;
        const isNotImplementationClient = this.props.client.clientStatusType !== IMPLEMENTATION.value;
        const isTerminatedClient = !userHasAccess(FINTECH) && this.props.client.clientStatusType === TERMINATED.value;
        const creditsInvoiceFrequencyOptions = this.generateCreditsInvoiceFrequencyOptions();
        const nbMonthsRetroactiveLimitationYear = this.state.retroactiveCredits.nbMonthsRetroactiveLimitation && convertMonthsToYears(this.state.retroactiveCredits.nbMonthsRetroactiveLimitation);
        return (
            <form onSubmit={handleSubmit}>
                {creditsInvoiceFrequencyOptions ? <div className="invoicing-config">
                    <h3 className="invoicing-form-title">
                        <FormattedMessage id="invoicingConfig.form.title" />
                    </h3>
                    <MessageBox className="alert-info">
                        <FormattedMessage id="invoicingConfig.form.description" values={{
                            year: nbMonthsRetroactiveLimitationYear
                        }} />
                    </MessageBox>
                    <div>
                        <Field
                            id="creditsInvoiceFrequency"
                            name="creditsInvoiceFrequency"
                            type="absNumber"
                            component={SelectInput}
                            required={isNotImplementationClient}
                            options={creditsInvoiceFrequencyOptions}
                            props={{ onChange: this.onChangeFrequency }}
                            label={<FormattedMessage id="invoicingConfig.form.creditsInvoiceFrequency" />}
                        />
                        <Field
                            id="creditsInvoiceCount"
                            name="creditsInvoiceCount"
                            type="absNumber"
                            component={TextInput}
                            disabled={true}
                            label={<FormattedMessage id="invoicingConfig.form.creditsInvoiceCount" />}
                        />
                        {this.state.frequency && this.state.invoiceCount &&
                            (<span className="frequency-info"><FormattedHTMLMessage id="invoicingConfig.form.calculatedInformation" values={{
                                frequency: this.state.frequency,
                                invoiceCount: this.state.invoiceCount,
                                year: nbMonthsRetroactiveLimitationYear
                            }} /></span>)
                        }
                    </div>

                    <button disabled={submitting || pristine || isTerminatedClient} type="submit" className="btn btn-lg btn-primary">
                        <FormattedMessage id="invoicingConfig.form.submit" />
                    </button>
                </div>
                    : <RectangleSpinner className="client-page-spinner" />
                }
            </form>
        );
    }
}

InvoicingConfigurationForm.propTypes = {
    client: PropTypes.object.isRequired,
    changeFieldValue: PropTypes.func,
    creditsInvoiceCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    submitting: PropTypes.bool,
    pristine: PropTypes.bool,
    handleSubmit: PropTypes.func,
    initialValues: PropTypes.object,
};

function mapDispatchToProps(dispatch) {
    return {
        changeFieldValue: bindActionCreators(changeFieldValue, dispatch),
        dataActions: bindActionCreators(dataActions, dispatch),
    };
}

function mapStateToProps(state) {
    const selector = formValueSelector("invoicingConfiguration");

    return {
        creditsInvoiceCount: selector(state, "creditsInvoiceCount"),
    };
}

export default connect(mapStateToProps, mapDispatchToProps, null, { withRef: true })(
    reduxForm({
        form: "invoicingConfiguration",
        validate: (values, props) =>
            validationUtils.validate(values, InvoicingConfigurationForm.generateValidationRules(props)),
    })(InvoicingConfigurationForm)
);



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/invoicing-configuration/InvoicingConfigurationForm.js